import React from 'react'
import { Grid, Typography } from '@mui/material'
import Box from 'components/Box'
import Condition from 'components/Condition'
import ConfirmButton from 'components/ConfirmButton'
import Video from 'components/Video'
import { Delete, Visibility, VisibilityOff } from '@mui/icons-material'
import { useQuickCommit } from 'hooks/useQuickCommit'
import { useSnackbar } from 'hooks/redux'
import { trackError } from 'services/analytics'
import { VideoAnswersField_UpdateVideoMutation } from '__generated__/VideoAnswersField_UpdateVideoMutation.graphql'
import { VideoAnswersField_DeleteVideoMutation } from '__generated__/VideoAnswersField_DeleteVideoMutation.graphql'
import { useFragment, graphql, useLazyLoadQuery } from 'react-relay'
import { useNodes } from 'hooks/relay'
import { VideoAnswersField_User$key } from '__generated__/VideoAnswersField_User.graphql'
import { VideoAnswersField_Query } from '__generated__/VideoAnswersField_Query.graphql'
import Suspense from 'components/Suspense'
import TabPlaceholder from '../../TabPlaceholder'
import useStyles from './styles'
import AddVideoAnswers from './components/AddVideoAnswers'

interface IVideoAnswersFieldProps {
  userId: string
  editable?: boolean
}

function VideoAnswersField(props: IVideoAnswersFieldProps) {
  const { classes } = useStyles()
  const { editable, userId } = props
  const { showSnackbarMessage } = useSnackbar()

  const data = useLazyLoadQuery<VideoAnswersField_Query>(graphql`
    query VideoAnswersField_Query($userId: ID!) {
      user(id: $userId) {
        ...VideoAnswersField_User
      }
    }
  `, { userId }, {
    fetchPolicy: 'store-and-network',
  })

  const user = useFragment(graphql`
    fragment VideoAnswersField_User on User {
      answers {
        id
        url
        public
        question {
          rawId
          title
        }
      }
    }
  `, data?.user as VideoAnswersField_User$key)

  const videos = useNodes(user?.answers)

  const { execute: updateVideoCommit } = useQuickCommit<VideoAnswersField_UpdateVideoMutation>(
    graphql`
      mutation VideoAnswersField_UpdateVideoMutation($input: UpdateVideoInput!) {
        updateVideo(input: $input) {
          video {
            id
            public

            user {
              ...VideoAnswersField_User # refresh data
            }
          }
        }
      }
    `,
  )

  const { execute: deleteVideoCommit } = useQuickCommit<VideoAnswersField_DeleteVideoMutation>(
    graphql`
      mutation VideoAnswersField_DeleteVideoMutation($input: DeleteVideoInput!) {
        deleteVideo(input: $input) {
          video {
            id @deleteRecord
          }
        }
      }
    `,
  )

  const handleSetVisibility = async (id: string, isPublic: boolean) => {
    try {
      await updateVideoCommit({
        input: {
          videoId: id,
          public: isPublic,
        },
      })

      showSnackbarMessage('Video updated', { level: 'success' })
    } catch (error) {
      trackError(error)
      showSnackbarMessage('Could not update video', { level: 'error' })
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteVideoCommit({
        input: {
          videoId: id,
        },
      })

      showSnackbarMessage('Video deleted', { level: 'success' })
    } catch (error) {
      trackError(error)
      showSnackbarMessage('Could not delete video', { level: 'error' })
    }
  }

  return (
    <Box data-cy="profile-video-answers">
      <Grid container spacing={2} data-cy="answers-list">
        {videos.map(v => (
          <Grid item xs={12} sm={12} md={6} key={v.url}>
            <div className={classes.item}>
              {editable && (
                <div className={classes.itemControls}>
                  <ConfirmButton
                    icon
                    tooltip={v.public ? 'Video is public - click to make it private' : 'Video is private - click to make it public'}
                    dialogTitle="Toggle visibility"
                    dialogMessage={v.public ? 'This will hide your video answer from your profile. Continue?' : 'This will make your video answer show up on your profile to the public. Continue?'}
                    data-cy={`toggle-visibility-answer-to-${v.question?.rawId}`}
                    onClick={() => handleSetVisibility(v.id, !v.public)}
                  >
                    {v.public ? <Visibility data-cy={`answer-to-${v.question?.rawId}-visible`} /> : <VisibilityOff data-cy={`answer-to-${v.question?.rawId}-hidden`} />}
                  </ConfirmButton>

                  <ConfirmButton
                    icon
                    tooltip="Delete video answer"
                    dialogTitle="Delete video answer"
                    data-cy={`delete-answer-to-${v.question?.rawId}`}
                    onClick={() => handleDelete(v.id)}
                  >
                    <Delete />
                  </ConfirmButton>
                </div>
              )}

              <Video videoUrl={v.url} style={{ width: '100%', height: 260 }} cover />

              <Typography variant="h6" className={classes.title}>
                {v.question?.title || 'Video Answer'}
              </Typography>
            </div>
          </Grid>
        ))}
      </Grid>

      <Condition condition={videos.length === 0}>
        <TabPlaceholder
          title="No video answers posted yet"
        />
      </Condition>

      <Condition condition={editable}>
        <Suspense>
          <AddVideoAnswers />
        </Suspense>
      </Condition>
    </Box>
  )
}

export default React.memo(VideoAnswersField)
